import type { IconProps } from "../Icon.props";
import createWebIcon from "../createWebIcon";
import validateKeys from "../validateKeys";

export default function Scan({ outline, ...props }: IconProps): JSX.Element {
  return outline ? <Scan.Outline {...props} /> : <Scan.Solid {...props} />;
}

Scan.Outline = function Outline({ name, ...props }: IconProps) {
  const Icon = createWebIcon({
    ...props,
    name: `${name}Outline`,
    path: (
      <>
        {/**
         * Enter the contents of your SVG here without the <svg> tag.
         * For outlined icons, be sure to add `className="icon-outline"`
         * to each path that needs to be outlined.
         */}
        <path
          d="M14.9419 3.3501C14.9419 2.93588 15.2777 2.6001 15.6919 2.6001H16.8969C19.373 2.6001 21.3799 4.60675 21.3799 7.0821V8.5951C21.3799 9.00931 21.0441 9.3451 20.6299 9.3451C20.2157 9.3451 19.8799 9.00931 19.8799 8.5951V7.0821C19.8799 5.43544 18.5448 4.1001 16.8969 4.1001H15.6919C15.2777 4.1001 14.9419 3.76431 14.9419 3.3501Z"
          fill="currentColor"
        />
        <path
          d="M0.75 12.8057C0.75 12.3915 1.08579 12.0557 1.5 12.0557H3.33151C3.3443 12.055 3.35717 12.0547 3.37012 12.0547C3.38307 12.0547 3.39594 12.055 3.40873 12.0557H20.5913C20.6041 12.055 20.6169 12.0547 20.6299 12.0547C20.6323 12.0547 20.6347 12.0547 20.6372 12.0547C20.6477 12.0548 20.6581 12.0551 20.6685 12.0557H22.5C22.9142 12.0557 23.25 12.3915 23.25 12.8057C23.25 13.2199 22.9142 13.5557 22.5 13.5557H21.3799V16.8787C21.3799 19.3549 19.3731 21.3617 16.8969 21.3617H15.6919C15.2777 21.3617 14.9419 21.0259 14.9419 20.6117C14.9419 20.1975 15.2777 19.8617 15.6919 19.8617H16.8969C18.5447 19.8617 19.8799 18.5265 19.8799 16.8787V13.5557H4.12012V16.8787C4.12012 18.5265 5.45533 19.8617 7.10312 19.8617H8.33912C8.75333 19.8617 9.08912 20.1975 9.08912 20.6117C9.08912 21.0259 8.75333 21.3617 8.33912 21.3617H7.10312C4.6269 21.3617 2.62012 19.3549 2.62012 16.8787V13.5557H1.5C1.08579 13.5557 0.75 13.2199 0.75 12.8057Z"
          fill="currentColor"
        />
        <path
          d="M4.12012 7.0821C4.12012 5.43544 5.4552 4.1001 7.10312 4.1001H8.33912C8.75333 4.1001 9.08912 3.76431 9.08912 3.3501C9.08912 2.93588 8.75333 2.6001 8.33912 2.6001H7.10312C4.62703 2.6001 2.62012 4.60675 2.62012 7.0821V8.5951C2.62012 9.00931 2.9559 9.3451 3.37012 9.3451C3.78433 9.3451 4.12012 9.00931 4.12012 8.5951V7.0821Z"
          fill="currentColor"
        />
      </>
    ),
  });

  return <Icon {...validateKeys(props)} />;
};

Scan.Solid = function Solid({ name, ...props }: IconProps) {
  const Icon = createWebIcon({
    ...props,
    name: `${name}Solid`,
    path: (
      <>
        {/* Secondary (lighter) path - scanner corners and ticket area */}
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M9.44312 4.2377C9.44312 3.82486 9.10213 3.49048 8.68213 3.49048H8.68113L7.05212 3.49146C4.86112 3.49342 3.07812 5.24379 3.07812 7.39327V9.25249C3.07812 9.66434 3.41912 9.9997 3.83912 9.9997C4.25912 9.9997 4.60113 9.66434 4.60113 9.25249V7.39327C4.60113 6.06652 5.70112 4.98687 7.05312 4.98491L8.68213 4.98393C9.10313 4.98393 9.44312 4.64955 9.44312 4.2377ZM17.448 3.49077H15.855C15.435 3.49077 15.094 3.82516 15.094 4.23799C15.094 4.64984 15.435 4.98325 15.855 4.98325H17.448C18.805 4.98325 19.909 6.06583 19.909 7.3965V9.25278C19.909 9.66463 20.25 10 20.67 10C21.091 10 21.431 9.66463 21.431 9.25278V7.3965C21.431 5.24213 19.645 3.49077 17.448 3.49077ZM9.49292 7.23314H15.0169C15.6219 7.23314 16.2029 7.47142 16.6259 7.89602C17.0519 8.32454 17.2879 8.90016 17.2869 9.4993V10.7447C17.2819 10.8908 17.1629 11.0094 17.0139 11.0134H7.49393C7.34592 11.0084 7.22692 10.8898 7.22393 10.7447V9.4993C7.21292 8.25884 8.22892 7.24588 9.49292 7.23314Z"
          className="icon-secondary"
        />
        {/* Primary (darker) path - scan line and frame */}
        <path
          d="M22.4934 12.5317H2.01639C1.59639 12.5317 1.25439 12.8661 1.25439 13.2789C1.25439 13.6908 1.59639 14.0242 2.01639 14.0242H3.07739V17.5877C3.07739 19.7372 4.86139 21.4875 7.05239 21.4895L8.68139 21.4905C9.10239 21.4905 9.44239 21.1561 9.44339 20.7433C9.44339 20.3314 9.10239 19.997 8.68239 19.997L7.05439 19.996C5.70139 19.9941 4.60039 18.9144 4.60039 17.5877V14.0242H7.22339V15.0156C7.21339 16.256 8.22839 17.27 9.49239 17.2817H15.0164C16.2814 17.27 17.2964 16.256 17.2864 15.0156V14.0242H19.9094V17.5838C19.9094 18.9154 18.8054 19.997 17.4484 19.997H15.8554C15.4344 19.997 15.0934 20.3314 15.0934 20.7433C15.0934 21.1561 15.4344 21.4905 15.8554 21.4905H17.4484C19.6444 21.4905 21.4314 19.7391 21.4314 17.5838V14.0242H22.4934C22.9134 14.0242 23.2544 13.6908 23.2544 13.2789C23.2544 12.8661 22.9134 12.5317 22.4934 12.5317Z"
          className="icon-primary"
        />
      </>
    ),
  });

  return <Icon {...validateKeys(props)} />;
};
